import {
  createErrorNotification,
  createWarningNotification,
  enqueueError,
  enqueueWarning,
} from '@paragon/notification-tools';

import { RootState } from '..';
import { getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import { getCommittedComponentDesigns } from '../selectors/getComponentDesignsSelectors';
import { isLongRunningAnalysisOngoing } from '../selectors/longRunningOperationSelectors';
import { enqueueDelayedNotification } from '../slices/delayedNotificationsSlice';
import {
  LongRunningAnalysisStage,
  recordErrorMessageDuringLongRunningAnalysis,
  updateComponentDesignStateInLongRunningAnalysis,
} from '../slices/longRunningOperationsSlice';
import { ThunkActionVoid } from './thunkActions';

export function updateComponentDesignStageInLongRunningAnalysis(
  componentDesignGuid: string,
  stage: LongRunningAnalysisStage,
) {
  return updateComponentDesignStateInLongRunningAnalysis({ componentDesignGuid, stage });
}

function getComponentDesignName(componentDesignGuid: string, state: RootState): string | null {
  const componentDesigns = getCommittedComponentDesigns(state);
  return componentDesigns.find((design) => design.guid === componentDesignGuid)?.component.name ?? null;
}

function withName(message: string, componentDesignGuid: string, state: RootState): string {
  const componentName = getComponentDesignName(componentDesignGuid, state);

  return componentName ? `${componentName}: ${message}` : message;
}

export const recordWarningDuringAnalysis =
  (componentDesignGuid: string, message: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const activeComponentDesignGuid = getActiveComponentDesignGuid(state);
    const notificationMessage = withName(message, componentDesignGuid, state);

    if (isLongRunningAnalysisOngoing(state)) {
      dispatch(
        recordErrorMessageDuringLongRunningAnalysis({
          componentDesignGuid,
          type: 'warning',
          message,
          fatal: false,
        }),
      );

      if (componentDesignGuid === activeComponentDesignGuid) {
        dispatch(enqueueWarning(notificationMessage));
      } else {
        dispatch(
          enqueueDelayedNotification({
            componentDesignGuid,
            notification: createWarningNotification(notificationMessage, true),
          }),
        );
      }
    } else {
      dispatch(enqueueWarning(notificationMessage));
    }
  };

export const recordErrorDuringAnalysis =
  (componentDesignGuid: string, message: string, options: { fatal: boolean }): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const activeComponentDesignGuid = getActiveComponentDesignGuid(state);
    const notificationMessage = withName(message, componentDesignGuid, state);

    if (isLongRunningAnalysisOngoing(state)) {
      dispatch(
        recordErrorMessageDuringLongRunningAnalysis({
          componentDesignGuid,
          type: 'error',
          message,
          fatal: options.fatal ?? false,
        }),
      );

      if (componentDesignGuid === activeComponentDesignGuid) {
        dispatch(enqueueError(notificationMessage));
      } else {
        dispatch(
          enqueueDelayedNotification({
            componentDesignGuid,
            notification: createErrorNotification(notificationMessage, true),
          }),
        );
      }
    } else {
      dispatch(enqueueError(notificationMessage));
    }
  };
